import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { MarketDialogueDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { CombinedDocuments } from "../../common/documents/CombinedDocuments";
import { isIncompleteFileUpload, type IncompleteFileUpload, UploadCompletedStatus } from "../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../common/documents/UppyUploader/useUppyUploader";

interface Props {
  dialogue: MarketDialogueDto;
  refetch: () => void;
}

export const MarketDialogueDocuments = ({ dialogue, refetch }: Props) => {
  const { t } = useTranslation();
  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityId: dialogue.id,
    entityType: "MarketDialogue",
    reloadEntity: refetch,
  });

  useEffect(() => {
    setDocuments(dialogue.documents);
  }, [dialogue.documents, setDocuments]);

  const onRemove = (document: ViewDocumentAsOwnerDto) =>
    removeUpload({
      status: UploadCompletedStatus.UploadCompleted,
      documentId: document.id,
      uploadId: document.fromUploadId,
      ...document,
    });

  return (
    <CombinedDocuments
      uploadedDocuments={documents}
      setUploadedDocuments={setDocuments}
      removeUploadedDocument={dialogue.canEdit ? onRemove : undefined}
      canDeleteDocuments={dialogue.canEdit}
      emptyState={t("No documents yet.")}
      documentUploadConfig={
        dialogue.canAddDocuments
          ? {
              uppy,
              dropTargetId: "marketDialogueFileTarget",
              dropTargetHelperText: dialogue.canEdit
                ? t("Upload draft tender documents or other material the suppliers should read")
                : t("A document you add now is shared with every participant at once"),
              uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
              removeUpload,
            }
          : undefined
      }
      isTagsColumnVisible={false}
    />
  );
};
